import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiHome,
    FiUsers,
    FiCreditCard,
    FiDollarSign,
    FiPlus,
    FiArrowRight,
    FiCheckSquare,
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import api from "../../../services/api";
import CollegeLogoBadge from "../../../components/common/CollegeLogoBadge";

export default function WardenDashboard() {
    const { user, collegeName } = useApp();
    const navigate = useNavigate();

    const [stats, setStats] = useState({
        totalHostels: 0,
        totalAllocations: 0,
        pendingFees: 0,
        recentCollections: 0,
    });
    const [recentAllocations, setRecentAllocations] = useState([]);
    const [recentReceipts, setRecentReceipts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const [hostelsRes, allocsRes, pendingRes, receiptsRes] = await Promise.allSettled([
                    api.get("/hostels", { params: { limit: 1 } }),
                    api.get("/hostel-allocations", { params: { limit: 5 } }),
                    api.get("/hostel-fees/pending", { params: { limit: 1 } }),
                    api.get("/hostel-fee-receipts", { params: { limit: 5 } }),
                ]);

                const totalHostels =
                    hostelsRes.status === "fulfilled"
                        ? hostelsRes.value.pagination?.total || hostelsRes.value.data?.total || hostelsRes.value.data?.length || 0
                        : 0;

                const allocDocs =
                    allocsRes.status === "fulfilled"
                        ? Array.isArray(allocsRes.value.data)
                            ? allocsRes.value.data
                            : allocsRes.value.data?.data || []
                        : [];

                const totalAllocations =
                    allocsRes.status === "fulfilled"
                        ? allocsRes.value.pagination?.total || allocDocs.length
                        : 0;

                const pendingData =
                    pendingRes.status === "fulfilled"
                        ? pendingRes.value.data?.totalPending || pendingRes.value.data?.total || 0
                        : 0;

                const receiptDocs =
                    receiptsRes.status === "fulfilled"
                        ? Array.isArray(receiptsRes.value.data)
                            ? receiptsRes.value.data
                            : receiptsRes.value.data?.data || []
                        : [];

                const totalCollected = receiptDocs.reduce(
                    (sum, r) => sum + (Number(r.amountPaid || r.amount) || 0),
                    0
                );

                setStats({
                    totalHostels,
                    totalAllocations,
                    pendingFees: pendingData,
                    recentCollections: totalCollected,
                });
                setRecentAllocations(allocDocs.slice(0, 5));
                setRecentReceipts(receiptDocs.slice(0, 5));
            } catch (err) {
                toast.error(err.message || "Failed to load warden dashboard data");
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading hostel dashboard…
            </div>
        );
    }

    return (
        <div>
            {/* ── Page Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "1rem", marginBottom: "1.25rem", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    <CollegeLogoBadge size={52} />
                    <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#003b67", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                            {collegeName}
                        </div>
                        <h1 className="page-title" style={{ margin: "2px 0 0 0" }}>Welcome back, {user?.displayName || user?.username} 🏢</h1>
                        <p className="page-subtitle" style={{ margin: "2px 0 0 0" }}>Hostel Administration &amp; Student Accommodation</p>
                    </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/warden/pending-fees")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiDollarSign size={14} /> Pending Dues
                    </button>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/warden/allocations")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiUsers size={14} /> View Allocations
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/warden/fee-collect")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiPlus size={14} /> Collect Hostel Fee
                    </button>
                </div>
            </div>

            {/* ── KPI Metric Cards ── */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                    gap: "1rem",
                    marginBottom: "1.5rem",
                }}
            >
                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/warden/hostels")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Total Hostels
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiHome size={15} color="#d97706" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                        {stats.totalHostels.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#d97706", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Hostel Buildings <FiArrowRight size={10} />
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/warden/allocations")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Active Allocations
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e0f2fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiUsers size={15} color="#0284c7" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                        {stats.totalAllocations.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#0284c7", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Resident Students <FiArrowRight size={10} />
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/warden/pending-fees")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Pending Hostel Dues
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiDollarSign size={15} color="#dc2626" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: stats.pendingFees > 0 ? "#dc2626" : "#16a34a" }}>
                        ₹{stats.pendingFees.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: stats.pendingFees > 0 ? "#dc2626" : "var(--text-muted)", marginTop: 4 }}>
                        {stats.pendingFees > 0 ? "Pending collection" : "All cleared ✓"}
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/warden/fee-receipts")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Recent Collections
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiCreditCard size={15} color="#16a34a" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "#16a34a" }}>
                        ₹{stats.recentCollections.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#16a34a", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Receipt Records <FiArrowRight size={10} />
                    </div>
                </div>
            </div>

            {/* ── Content Grid: Recent Allocations & Receipts ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.25rem" }}>
                {/* Recent Room Allocations */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                            <FiCheckSquare size={14} color="#d97706" /> Recent Allocations
                        </span>
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={() => navigate("/warden/allocations")}
                            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12 }}
                        >
                            View All <FiArrowRight size={11} />
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {recentAllocations.length === 0 ? (
                            <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                No recent room allocations found.
                            </div>
                        ) : (
                            recentAllocations.map((item, idx) => (
                                <div
                                    key={item._id || idx}
                                    style={{
                                        padding: "0.75rem 1rem",
                                        borderBottom: "1px solid var(--border)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        fontSize: 13,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{item.student?.fullName || "Student"}</div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                            {item.hostel?.name || "Hostel"} · Room {item.room?.roomNumber || "—"} (Bed {item.bedNumber || "1"})
                                        </div>
                                    </div>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            padding: "2px 8px",
                                            borderRadius: 99,
                                            background: item.status === "Active" ? "#dcfce7" : "#f1f5f9",
                                            color: item.status === "Active" ? "#15803d" : "#64748b",
                                        }}
                                    >
                                        {item.status || "Active"}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Recent Hostel Fee Receipts */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                            <FiCreditCard size={14} color="#16a34a" /> Recent Fee Receipts
                        </span>
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={() => navigate("/warden/fee-receipts")}
                            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12 }}
                        >
                            View All <FiArrowRight size={11} />
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {recentReceipts.length === 0 ? (
                            <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                No hostel fee receipts found.
                            </div>
                        ) : (
                            recentReceipts.map((item, idx) => (
                                <div
                                    key={item._id || idx}
                                    style={{
                                        padding: "0.75rem 1rem",
                                        borderBottom: "1px solid var(--border)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        fontSize: 13,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{item.student?.fullName || "Student"}</div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                            Receipt #{item.receiptNo || "—"} · Date: {item.paymentDate ? item.paymentDate.slice(0, 10) : "—"}
                                        </div>
                                    </div>
                                    <span style={{ fontWeight: 700, color: "#16a34a" }}>
                                        ₹{(item.amountPaid || item.amount || 0).toLocaleString("en-IN")}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}